<template>
  <pdl-loading :is-loading="isLoading">
    <div class="project-one-paint-and-theme-picker grid-container">
      <project-one-header :title="bp('chooseTheme')" :message="bp('chooseThemeMessage')">
        <div class="project-one-theme-selector">
          <!-- Theme filter section -->
          <project-one-theme-selector-list
            v-if="themeChoices"
            :initial-selected-theme-id="selectedThemeId"
            :base-properties="baseProperties"
            :theme-choices="themeChoices"
            @selected-theme="handleSelectedThemeEvent"
          />

          <pdl-loading :is-loading="isLoadingInspirations">
            <!-- Inspirations section -->
            <ul
              v-if="inspirations && inspirations.length"
              qaid="project-one-inspiration-selection-card-list"
              class="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-8 mt-6"
            >
              <li
                v-for="(inspiration, index) in inspirations"
                :key="index"
                qaid="project-one-inspiration-selection-card"
                @click="navigateToThemeCustomizer(inspiration)"
              >
                <pdl-card
                  qaid="project-one-inspiration-selection-card-pdl-card"
                  class="project-one-inspiration-card cursor-pointer"
                  tabindex="0"
                >
                  <responsive-image
                    slot="image"
                    qaid="inspiration-selection-card__img"
                    :src="inspiration.p1ImageUrl"
                    sizes="(max-width: 640) 100vw, (max-width: 1024) 50vw, 33vw"
                    :alt="inspiration.p1PresetName"
                  />
                  <template slot="bodyContent">
                    <h3
                      qaid="inspiration-selection-card__title"
                      class="font-medium text-sm leading-tight mt-1 mb-25"
                      v-html="inspiration.p1PresetName"
                    />
                  </template>
                </pdl-card>
              </li>
            </ul>
          </pdl-loading>
        </div>
      </project-one-header>
    </div>
  </pdl-loading>
</template>

<script>
import axios from 'axios';
import ProjectOneHeader from '@/projectOne/components/shared/ProjectOneHeader';
import ProjectOneThemeSelectorList from '@/projectOne/components/paintAndTheme/components/ProjectOneThemeSelectorList';
import ResponsiveImage from '@/components/ResponsiveImage';
import {PdlCard} from '@pedal/pdl-card';
import {PdlLoading} from '@pedal/pdl-loading';

export default {
  components: {
    ProjectOneHeader,
    ProjectOneThemeSelectorList,
    ResponsiveImage,
    PdlCard,
    PdlLoading,
  },

  data() {
    return {
      inspirations: null,
      isLoading: false,
      isLoadingInspirations: false,
      selectedThemeId: null,
      themeChoices: null,
    };
  },

  created() {
    this.fetchThemeData();
  },

  methods: {
    handleSelectedThemeEvent(themeId) {
      this.fetchInspirationsById(themeId);
    },

    fetchThemeData() {
      this.isLoading = true;

      const id = this.$route.params.buildId;
      const buildEndpointType = id?.startsWith('P-') ? 'configurations' : 'packages';
      let themeUrl = `/project-one/api/${buildEndpointType}/${id}/themes/`;

      axios
        .get(themeUrl)

        .then((response) => {
          const responseTheme = response?.data?.data?.theme;
          // The initial /themes call returns inspirations from the default theme so
          // there is no need to call fetchInspirationsById on load
          this.inspirations = response?.data?.data?.inspirations;

          if (responseTheme) {
            // only keep unique marketing theme groups (i.e. ICON)
            this.themeChoices = this.filterArrayByKeyValue(responseTheme.choices, 'marketingThemeGroup');
            this.selectedThemeId = responseTheme.defaultChoiceId;
          }
        })
        .finally(() => (this.isLoading = false))
        .catch((error) => {
          console.error(error);
        });
    },

    fetchInspirationsById(themeId) {
      this.isLoadingInspiration = true;
      const id = this.$route.params.buildId;
      const buildEndpointType = id?.startsWith('P-') ? 'configurations' : 'packages';
      let themeUrl = `/project-one/api/${buildEndpointType}/${id}/themes/`;

      axios
        .get(`${themeUrl}/${themeId}/`)
        .then((response) => {
          this.inspirations = response?.data?.data?.inspirations;
        })
        .finally(() => (this.isLoadingInspiration = false))
        .catch((error) => {
          console.error(error);
        });
    },

    filterArrayByKeyValue(arr, key) {
      /**
       * Pass in the array and the key (as a string) of the value you would like to remove duplicates of.
       * Not good for deep nested arrays
       * */
      return [...new Map(arr.map((item) => [item[key], item])).values()];
    },

    navigateToThemeCustomizer(inspiration) {
      this.$router.push({name: 'paintTheme', query: {presetPK: inspiration.pk}}).catch((error) => {
        if (error.name != 'NavigationDuplicated') {
          throw error;
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.project-one-paint-and-theme-picker {
  display: grid;
  grid-template-rows: 1fr;
  grid-template-columns: minmax(0, 1fr);
}

.project-one-inspiration-card {
  @apply border border-transparent;

  transition: box-shadow 0.4s cubic-bezier(0, 0, 0.2, 1);

  &:focus {
    @apply border border-blue;
  }

  &:hover {
    @apply shadow-md;
  }
}
</style>
